/* eslint-disable @typescript-eslint/no-empty-function */
import { useResponsive } from "@/hooks";
import { useMemo } from "react";
import useVariableValues from "./useVariableValues";
import { useNode } from "@craftjs/core";

export enum MediaSize {
  SM = "sm",
  MD = "md",
  LG = "lg",
}

export type ResponsiveValue<T> = Partial<Record<MediaSize, T>>;

const useResponsiveValues = () => {
  const { isTablet, isDesktop } = useResponsive();
  const vv = useVariableValues();
  const {
    props,
    actions: { setProp },
  } = useNode((node) => ({
    props: node.data.props,
  }));

  const viewport = useMemo(() => {
    const qs = new URLSearchParams(document.location.search);
    if (qs.has("viewport")) {
      return qs.get("viewport");
    }

    if (isTablet) return MediaSize.MD;
    if (isDesktop) return MediaSize.LG;

    return MediaSize.SM;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document.location.search, isDesktop, isTablet]) as MediaSize;

  const setValue = <T>(value?: T | ResponsiveValue<T>, newValue?: T) => {
    if (value !== undefined && typeof value === "object") {
      return {
        ...value,
        [viewport]: newValue === "" || newValue === null ? undefined : newValue,
      };
    }

    return {
      [viewport]: newValue === "" || newValue === null ? undefined : newValue,
    };
  };

  const toggleValue = <T>(value?: T | ResponsiveValue<T>, newValue?: T) => {
    if (value !== undefined && typeof value === "object") {
      return {
        ...value,
        [viewport]: value[viewport] === newValue ? undefined : newValue,
      };
    }

    return {
      [viewport]: newValue,
    };
  };

  const getValue = <T>(value?: T | ResponsiveValue<T>, defaultValue?: any) => {
    if (!value && !defaultValue) return undefined;
    if (!value && defaultValue) return defaultValue;
    if (typeof value !== "object") return value;

    if (viewport === MediaSize.SM) {
      if (value?.sm) return vv.getValue(value.sm);
      if (value?.md) return vv.getValue(value.md);
      if (value?.lg) return vv.getValue(value.lg);
    }

    if (viewport === MediaSize.MD) {
      if (value?.md) return vv.getValue(value.md);
      if (value?.sm) return vv.getValue(value.sm);
      if (value?.lg) return vv.getValue(value.lg);
    }

    if (viewport === MediaSize.LG) {
      if (value?.lg) return vv.getValue(value.lg);
      if (value?.md) return vv.getValue(value.md);
      if (value?.sm) return vv.getValue(value.sm);
    }

    return defaultValue ?? undefined;
  };

  return {
    viewport,
    setValue,
    getValue,
    toggleValue,
  };
};

export default useResponsiveValues;
